import { union } from 'lodash';

/** A supplier id as this screen holds it — a Mongo id already serialised to a string. */
export type SupplierId = string;

/**
 * A supplier id exactly as it can arrive from the API: normally an already-serialised string, but
 * a populated / non-serialised document reaches the client as an object whose `toString()` yields
 * the same id, which is what the dropdown matches on.
 */
export interface SerialisableId {
  toString(): string;
}

/**
 * A supplier id list off the product payload. The API omits the field on products that have never
 * had a supplier, and legacy rows can carry blank entries, so both are part of the contract.
 */
export type SupplierIdPayload = Array<SupplierId | SerialisableId | null | undefined> | null | undefined;

/**
 * Normalises a raw id list from the API into plain string ids, dropping empty entries.
 * @param ids Raw value off the product payload.
 * @returns String ids, in source order.
 */
const toIdList = (ids: SupplierIdPayload): SupplierId[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  return ids.filter((id): id is SupplierId | SerialisableId => id !== null && id !== undefined && id !== '').map((id) => String(id));
};

/**
 * Folds the suppliers a product already consumes downstream into its own supplier selection.
 *
 * `orderSupplierId` (GET /development-orders/:id, built by `getSuppliersInUseForPO`) lists every
 * supplier reached through a Purchase Order, Product Costing row or Sample. Those rows are rendered
 * disabled in the Supplier(s) dropdown, while the tick state comes from `suppliers` — so a supplier
 * attached from one of those modules without ever being picked on the product itself rendered
 * disabled AND unticked. Merging on hydrate keeps `suppliers` a superset of `orderSupplierId`, which
 * is the invariant the retain-on-deselect branch and the factory recalculation already assume.
 *
 * `retrieveDevelopmentOrderById` merges the same two lists before it responds, so this is a guard
 * rather than the primary fix — it keeps the screen correct against an older API build.
 *
 * @param savedSuppliers The product's own `suppliers` array.
 * @param inUseSuppliers The `orderSupplierId` array returned alongside it.
 * @returns De-duplicated string ids — saved selection first, downstream-only suppliers appended.
 */
export const mergeInUseSuppliers = (savedSuppliers: SupplierIdPayload, inUseSuppliers: SupplierIdPayload): SupplierId[] =>
  union(toIdList(savedSuppliers), toIdList(inUseSuppliers));
